import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Icon, Label, Loader, Message, Popup, Table } from "semantic-ui-react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { IOutreachRule, IOutreachRuleSubmission } from "../../app/interfaces/IOutreachRule";
import { getCompanyEmployeesThunk, selectCompanyEmployees } from "../../slices/companyEmployeesSlice";
import {
    clearOutreachRulesError,
    createOutreachRuleThunk,
    deleteOutreachRuleThunk,
    getOutreachRulesThunk,
    getProjectStagesThunk,
    selectOutreachRulesState,
    updateOutreachRuleThunk,
} from "../../slices/outreachRulesSlice";
import { selectUser } from "../../slices/userSlice";
import { StyledSegment } from "../CommonStyledComponents";
import OutreachRuleForm from "./OutreachRuleForm";

/*
* Set up who gets reminded to rate a project's subs when that project reaches a
* given status in Procore.
*
* Rendered on the company page with no projectId, where rules cover every
* project, and on a project page with one, where a rule overrides the company
* rule for that project alone.
*/
const OutreachRules = ({ projectId }: { projectId?: number }) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const companyId = parseInt(params?.companyId!);
    const { data: rules, stages, status, error } = useAppSelector(selectOutreachRulesState);
    const employees = useAppSelector(selectCompanyEmployees);
    const user = useAppSelector(selectUser);
    const isAdmin = user?.companies?.find(c => c.id === companyId)?.isAdmin === true;

    const [inCreateMode, setInCreateMode] = useState<boolean>(false);
    const [ruleToEdit, setRuleToEdit] = useState<null | IOutreachRule>(null);

    useEffect(() => {
        dispatch(getOutreachRulesThunk({ companyId, projectId }));
        dispatch(getProjectStagesThunk(companyId));
        dispatch(getCompanyEmployeesThunk(companyId));
    }, [companyId, projectId]);

    const closeForms = () => {
        setInCreateMode(false);
        setRuleToEdit(null);
        dispatch(clearOutreachRulesError());
    };

    const createRule = (rule: IOutreachRuleSubmission) => {
        dispatch(createOutreachRuleThunk({ companyId, rule })).then(closeForms);
    };

    const editRule = (rule: IOutreachRuleSubmission) => {
        if (!ruleToEdit) {
            return;
        }
        dispatch(updateOutreachRuleThunk({ companyId, ruleId: ruleToEdit.id, rule })).then(closeForms);
    };

    const toggleRule = (rule: IOutreachRule) => {
        dispatch(updateOutreachRuleThunk({
            companyId,
            ruleId: rule.id,
            rule: {
                stageId: rule.stageId,
                projectId: rule.projectId,
                isActive: !rule.isActive,
                notifyAllEmployees: rule.notifyAllEmployees,
                recipientIds: rule.recipients.map(r => r.id),
            },
        }));
    };

    // On a project page the list also carries the company-wide rules, which are
    // read only here - they are edited on the company page.
    const ownRules = rules.filter(rule => projectId ? rule.projectId === projectId : rule.projectId === null);
    const inheritedRules = projectId ? rules.filter(rule => rule.projectId === null) : [];
    const overriddenStageIds = ownRules.map(rule => rule.stageId);

    const describeRecipients = (rule: IOutreachRule) => {
        if (rule.notifyAllEmployees) {
            return 'Everyone at the company';
        }
        if (!rule.recipients.length) {
            return 'Nobody yet';
        }
        return rule.recipients.map(r => `${r.firstName} ${r.lastName}`).join(', ');
    };

    if (status === 'loading' && !rules.length && !stages.length) {
        return <Loader active inline='centered' />;
    }

    return (
        <StyledOutreachRules id='outreach-rules'>
            <p>
                {
                    projectId
                        ? 'Reminders to rate this project\'s subs when it reaches a status. A rule here replaces the company rule for the same status.'
                        : 'When a project reaches one of these statuses in Procore, Sub Ratings reminds the people below to rate the subs who worked on it.'
                }
            </p>

            { error?.reason && <Message negative size='tiny' content={error.reason} /> }

            {
                !stages.length &&
                <Message
                    info
                    size='tiny'
                    content="We have not seen any project statuses for this company yet. They arrive with your projects from Procore."
                />
            }

            <Table basic='very' compact>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Project status</Table.HeaderCell>
                        <Table.HeaderCell>Who gets reminded</Table.HeaderCell>
                        <Table.HeaderCell>Status</Table.HeaderCell>
                        { isAdmin && <Table.HeaderCell textAlign='right'>&nbsp;</Table.HeaderCell> }
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {
                        ownRules.map(rule => (
                            ruleToEdit?.id === rule.id ?
                                <Table.Row key={rule.id}>
                                    <Table.Cell colSpan={isAdmin ? 4 : 3}>
                                        <OutreachRuleForm
                                            rule={rule}
                                            stages={stages}
                                            employees={employees}
                                            projectId={projectId}
                                            takenStageIds={overriddenStageIds}
                                            cancel={closeForms}
                                            save={editRule}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                                :
                                <Table.Row key={rule.id}>
                                    <Table.Cell>{rule.stageName}</Table.Cell>
                                    <Table.Cell>{describeRecipients(rule)}</Table.Cell>
                                    <Table.Cell>
                                        <Label size='tiny' color={rule.isActive ? 'green' : undefined}>
                                            {rule.isActive ? 'On' : 'Off'}
                                        </Label>
                                    </Table.Cell>
                                    {
                                        isAdmin &&
                                        <Table.Cell textAlign='right'>
                                            <Popup
                                                content={rule.isActive ? 'Stop these reminders' : 'Start these reminders'}
                                                trigger={
                                                    <Button
                                                        size='mini'
                                                        icon={rule.isActive ? 'pause' : 'play'}
                                                        onClick={() => toggleRule(rule)}
                                                    />
                                                }
                                            />
                                            <Button size='mini' icon='pencil' onClick={() => { closeForms(); setRuleToEdit(rule); }} />
                                            <Button
                                                size='mini'
                                                icon='trash'
                                                onClick={() => dispatch(deleteOutreachRuleThunk({ companyId, ruleId: rule.id }))}
                                            />
                                        </Table.Cell>
                                    }
                                </Table.Row>
                        ))
                    }
                    {
                        inheritedRules
                            .filter(rule => !overriddenStageIds.includes(rule.stageId))
                            .map(rule => (
                                <Table.Row key={`company-${rule.id}`} disabled>
                                    <Table.Cell>{rule.stageName}</Table.Cell>
                                    <Table.Cell>{describeRecipients(rule)}</Table.Cell>
                                    <Table.Cell>
                                        <Label size='tiny' basic>
                                            {rule.isActive ? 'On, from company' : 'Off, from company'}
                                        </Label>
                                    </Table.Cell>
                                    { isAdmin && <Table.Cell /> }
                                </Table.Row>
                            ))
                    }
                    {
                        !ownRules.length && !inheritedRules.length &&
                        <Table.Row>
                            <Table.Cell colSpan={isAdmin ? 4 : 3}>No reminders set up yet.</Table.Cell>
                        </Table.Row>
                    }
                </Table.Body>
            </Table>

            {
                isAdmin && (
                    inCreateMode ?
                        <OutreachRuleForm
                            stages={stages}
                            employees={employees}
                            projectId={projectId}
                            takenStageIds={overriddenStageIds}
                            cancel={closeForms}
                            save={createRule}
                        />
                        :
                        <StyledSegment compact>
                            <p>Add a reminder for a project status</p>
                            <Button
                                floated='right'
                                icon
                                disabled={!stages.length}
                                onClick={() => { closeForms(); setInCreateMode(true); }}
                            >
                                <Icon name='plus' />
                            </Button>
                        </StyledSegment>
                )
            }

            {
                !isAdmin &&
                <p><Icon name='lock' /> Only a company admin can change these.</p>
            }
        </StyledOutreachRules>
    );
};

const StyledOutreachRules = styled('div')`
    max-width: 750px;
`;

export default OutreachRules;
